import { compact, get, isNil, isString, size, defaultTo, round } from 'lodash'
import i18n from 'simple-react-i18n'
import moment from 'moment'

const FTP_PATH_REGEX = /ftp\/[^/]+\/(.*)/

const isFolder = (item) => item?.fileType === 'folder'

const getIconFromFileType = (fileType) => {
    switch (fileType) {
        case 'folder': return 'folder'
        case 'csv':
        case 'xlsx':
        case 'xls': return 'table_chart'
        case 'pdf': return 'picture_as_pdf'
        case 'script': return 'terminal'
        case 'image': return 'image'
        default: return 'insert_drive_file'
    }
}

const getColorFromFileType = (fileType) => {
    switch (fileType?.toLowerCase()) {
        case 'folder': return '#FFCA28'
        case 'pdf': return '#e53935'
        case 'csv':
        case 'xlsx':
        case 'xls': return '#43a047'
        case 'image':
        case 'jpg':
        case 'jpeg':
        case 'png':
        case 'gif': return '#0697feff'
        default: return '#bdbdbd'
    }
}

const formatSize = (fileSize) => {
    if (isNil(fileSize) || fileSize === 0) return '—'
    if (fileSize >= 1024 * 1024) return `${round(fileSize / (1024 * 1024))} Mo`
    if (fileSize >= 1024) return `${round(fileSize / 1024)} Ko`
    return `${fileSize} o`
}
const formatDate = (modifiedAt) =>
    isNil(modifiedAt) ? '—' : moment(modifiedAt).format('DD MMM YYYY')

const normalizePath = (...parts) => {
    const joined = compact(parts).join('/').replace(/\/+/g, '/')
    return joined.startsWith('/') ? joined : `/${joined}`
}

const flattenTree = (node) => {
    if (isNil(node)) return []
    return [node, ...defaultTo(node.children, []).reduce((acc, child) => [...acc, ...flattenTree(child)], [])]
}

const getDownloadPayload = (item) => {
    const downloadName = defaultTo(get(item, 'label'), '')
    const cleanPath = String(get(item, 'path') || get(item, 'id') || '').replace(/^\//, '')
    const standardPath = cleanPath.replace(/\\/g, '/')
    const match = standardPath.match(FTP_PATH_REGEX)
    const relativePath = match ? match[1] : standardPath
    return { downloadName, relativePath }
}

const getFolderTooltip = (item) => {
    if (get(item, 'fileCountText')) return item.fileCountText
    const childCount = size(defaultTo(item.children, []))
    return `${childCount} ${i18n.elements}`
}

const parseLabel = (rawLabel) => {
    const countMatch = rawLabel.match(/\s*\(\s*(\d+[^)]*)\)\s*$/)
    return countMatch
        ? { label: rawLabel.replace(/\s*\(\s*\d+[^)]*\)\s*$/, ''), fileCountText: countMatch[1] }
        : { label: rawLabel, fileCountText: null }
}

const getRelativeId = (standardizedRawId) => {
    const match = standardizedRawId.match(FTP_PATH_REGEX)
    if (match) return match[1]
    if (standardizedRawId.match(/ftp\/[^/]+$/)) return '/'
    return standardizedRawId
}

const enrichTree = (item, parentPath = '', depth = 0, parent = null) => {
    const rawId = defaultTo(get(item, 'id'), '')
    const { label: rawLabel, fileCountText } = parseLabel(get(item, 'label') || rawId)

    const standardizedRawId = isString(rawId) ? rawId.replace(/\\/g, '/') : ''
    const relativeId = getRelativeId(standardizedRawId)

    const path = normalizePath('/', relativeId)
    const node = { ...item, id: rawId, label: rawLabel, fileCountText, depth, path, parent, children: [] }
    node.children = defaultTo(get(item, 'children'), []).map((child) => enrichTree(child, path, depth + 1, node))
    return node
}

const buildBreadcrumb = (node) => {
    if (isNil(node)) return []
    return [...buildBreadcrumb(get(node, 'parent')), node]
}

export {
    FTP_PATH_REGEX,
    isFolder,
    getIconFromFileType,
    getColorFromFileType,
    formatSize,
    formatDate,
    normalizePath,
    flattenTree,
    getDownloadPayload,
    getFolderTooltip,
    enrichTree,
    buildBreadcrumb,
}
